<template>
  <page>
    <review-provider
      v-model="review"
      :review-query="GetReviewForFullTextPage"
      subscribe-to-upload-updates
      :loading.sync="loading"
    />
    <template #header-bar>
      <page-header-bar
        v-if="!loading"
        :root-route="reviewPageRoute"
        class="pl-9"
        breadcrumb="Full Text Review"
      >
        {{ review.title }}
        <template v-if="!loading" #actions>
          <div class="d-flex align-center">
            <action-button
              color="primary"
              class="mr-2"
              @click="triggerBulkUpload"
            >
              <v-icon class="material-icons-outlined" left>
                upload_file
              </v-icon>
              Bulk Upload Full Text PDFs
            </action-button>
            <action-button
              color="primary"
              class="mr-2"
              :loading="bulkFetchLoading"
              :disabled="bulkFetchLoading"
              @click="triggerBulkFetch"
            >
              <v-icon class="material-icons-outlined" left>
                cloud_download
              </v-icon>
              Auto-fetch PDFs for Review
            </action-button>
            <settings-button :to="`/review/${review.id}/settings`" />
          </div>
        </template>
      </page-header-bar>
      <page-header-bar v-else>
        <v-skeleton-loader width="400" type="text" />
      </page-header-bar>
    </template>
    <template #content>
      <page-tabs
        v-if="review"
        v-model="selectedTabIndex"
        :tabs="tabs"
        use-tab-routes
      >
        <page-tab-panel>
          <review-tab
            :review="review"
            :current-researcher="currentResearcher"
          />
        </page-tab-panel>
      </page-tabs>
      <v-snackbar
        :value="!!bulkFetchMessage"
        color="success"
        timeout="8000"
        @input="bulkFetchMessage = null"
      >
        {{ bulkFetchMessage }}
      </v-snackbar>
      <v-snackbar
        :value="!!bulkFetchError"
        color="error"
        timeout="8000"
        @input="bulkFetchError = null"
      >
        {{ bulkFetchError }}
      </v-snackbar>
      <bulk-upload-full-text-pdf-controller
        v-if="review"
        :review="review"
      />
    </template>
  </page>
</template>

<script>
import Page from '@/components/Common/Page';
import PageHeaderBar from '@/components/Common/PageHeaderBar';
import ReviewProvider from '@/components/Providers/ReviewProvider';
import PageTabs from '@/components/Common/PageTabs';
import PageTabPanel from '@/components/Common/PageTabPanel';
import ReviewTab from '@/components/FullTextReviewPageV2/ReviewTab/ReviewTab';
import SettingsButton from '@/components/Common/SettingsButton';
import ActionButton from '@/components/Common/ActionButton';
import BulkUploadFullTextPdfController from '@/components/BulkUploadFullTextPdfController/BulkUploadFullTextPdfController';
import { Researcher } from '@/models';
import { mapActions } from 'vuex';
import {
  GetCurrentResearcher,
  CreateBulkFetchFullTextJobs,
  GetReviewForFullTextPage,
} from '@/graphql';

export default {
  name: 'FullTextReviewPageV2',
  components: {
    Page,
    PageHeaderBar,
    ReviewProvider,
    PageTabs,
    PageTabPanel,
    ReviewTab,
    SettingsButton,
    ActionButton,
    BulkUploadFullTextPdfController,
  },
  data: () => ({
    review: null,
    loading: true,
    tabs: ['Review'],
    selectedTabIndex: 0,
    currentResearcher: null,
    GetReviewForFullTextPage,
    bulkFetchLoading: false,
    bulkFetchMessage: null,
    bulkFetchError: null,
  }),
  apollo: {
    currentResearcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
  },
  computed: {
    reviewPageRoute() {
      return `/review/${
        this.$route.params.reviewId ?? this.review.id
      }`;
    },
  },
  methods: {
    ...mapActions({
      triggerBulkUpload: 'bulkUploadFullTextPdf/triggerWorkflow',
    }),
    redirectToReviewsPage() {
      this.$router.push('/');
    },
    async triggerBulkFetch() {
      this.bulkFetchLoading = true;
      this.bulkFetchMessage = null;
      this.bulkFetchError = null;
      try {
        const result = await this.$apollo.mutate({
          mutation: CreateBulkFetchFullTextJobs,
          variables: {
            reviewId: this.$route.params.reviewId,
          },
          // Match the single-reference auto-fetch (fullTextPdf store) so the
          // visible reference lists pick up the new in-progress fetch jobs.
          refetchQueries: [
            'GetReviewForFullTextPage',
            'GetReviewForReviewPage',
            'GetReferencesByReviewIdAndFilters',
            'GetReferencesByFilters',
            'GetReferencesPendingFullTextReview',
            'GetReferencesAwaitingYouFullTextReview',
            'GetReferencesFullTextReviewed',
            'GetReferencesForFullTextReviewConflictReview',
          ],
        });

        const {
          jobsCreated,
          alreadyHavePdf,
          missingDoi,
        } = result.data.createBulkFetchFullTextJobs;

        let message = `Started fetching PDFs for ${jobsCreated} reference${
          jobsCreated !== 1 ? 's' : ''
        }.`;
        if (alreadyHavePdf > 0) {
          message += ` ${alreadyHavePdf} already have PDFs.`;
        }
        if (missingDoi > 0) {
          message += ` ${missingDoi} missing DOI (cannot fetch).`;
        }

        this.bulkFetchMessage = message;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error triggering bulk fetch:', error);
        this.bulkFetchError =
          'Error starting bulk PDF fetch. Please try again or contact support.';
      } finally {
        this.bulkFetchLoading = false;
      }
    },
  },
};
</script>
